// Site surveys: the rules the API and the screens share (migration 0408).
//
// One survey is one room or area on one opportunity (or on a bid before the
// win). The room survey is the AV / IT Site Survey form the engineers already
// fill in: nine questions, where a blank answer means the standard assumption
// holds and the job is priced that way. So the useful output of a survey is
// what it says is NOT standard, which `deviations` lists.

export const SURVEYS_PAGE = 'surveys';
export const SURVEYS_REVIEW_KEY = 'surveys:review';

export const SURVEY_TYPES = ['room', 'desks'];
// What a person chooses and sees. A desk move is stored as a desk survey of
// the kind 'move' (migration 0410), so the API takes and returns `kind`.
export const KINDS = ['room', 'desks', 'moves'];
export const KIND_LABELS = { room: 'Room (AV / IT)', desks: 'Desk standardisation', moves: 'Desk move' };
export const SURVEY_TYPE_LABELS = KIND_LABELS;
export const kindOf = (s) => (s?.kind || (s?.survey_type === 'desks' && s?.desk_kind === 'move' ? 'moves' : s?.survey_type) || 'room');

export const STATUSES = ['requested', 'booked', 'in_progress', 'submitted', 'reviewed', 'cancelled'];
export const STATUS_LABELS = {
  requested: 'Requested',
  booked: 'Booked',
  in_progress: 'In progress',
  submitted: 'Submitted',
  reviewed: 'Reviewed',
  cancelled: 'Cancelled',
};
// Tinted badges, written for light mode; styles.css remaps them for dark.
export const STATUS_TONES = {
  requested: 'bg-gray-100 text-gray-700',
  booked: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  submitted: 'bg-purple-50 text-purple-700',
  reviewed: 'bg-green-50 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};
export const OPEN_STATUSES = ['requested', 'booked', 'in_progress', 'submitted'];
// Once reviewed or cancelled, the answers are the record and stay as they are.
export const LOCKED_STATUSES = ['reviewed', 'cancelled'];

// Which moves are allowed, and which need the review key.
export const MOVES = {
  booked:      { from: ['requested', 'in_progress'], review: false },
  in_progress: { from: ['requested', 'booked', 'submitted', 'reviewed'], review: false },
  submitted:   { from: ['requested', 'booked', 'in_progress'], review: false },
  reviewed:    { from: ['submitted'], review: true },
  cancelled:   { from: ['requested', 'booked', 'in_progress', 'submitted'], review: false },
};
// Sending a submitted survey back, or reopening a reviewed one, is the reviewer's call.
export const moveNeedsReview = (from, to) => !!MOVES[to]?.review || (to === 'in_progress' && (from === 'submitted' || from === 'reviewed'));

export const SECTIONS = ['task', 'network', 'current_kit', 'needed_kit', 'risk'];
export const SECTION_LABELS = {
  task: 'What needs doing',
  network: 'Network details',
  current_kit: 'Current kit in the room',
  needed_kit: 'Kit and materials needed',
  risk: 'Anything that could stop it, delay it or cost extra',
};
export const NETWORKS = ['RDN', 'RDC', 'NNPP', 'BB'];

export const CAN_DO_LABELS = { as_asked: 'Yes, as asked', differently: 'Yes, but differently', no: 'No' };
export const HOURS_LABELS = { in: 'In hours', out: 'Out of hours', either: 'Either' };
export const VISIT_LABELS = { single: 'One visit', phased: 'Phased' };
export const HEIGHT_LABELS = { no: 'No', ladder: 'Ladder', tower: 'Tower', mewp: 'MEWP' };
export const YES_NO_LABELS = { yes: 'Yes', no: 'No' };
export const LIVE_LABELS = { yes: 'Yes', no: 'No', unknown: "Don't know" };
export const SURFACES = ['plasterboard', 'block', 'concrete', 'steel', 'other'];
export const SURFACE_LABELS = { plasterboard: 'Plasterboard', block: 'Block', concrete: 'Concrete', steel: 'Steel', other: 'Other' };
export const PERMITS = ['permit', 'escort', 'ecr', 'sia'];
export const PERMIT_LABELS = { permit: 'Permit', escort: 'Escort', ecr: 'ECR', sia: 'SIA' };
export const SITE_FLAGS = ['asbestos', 'restricted', 'lift', 'parking', 'out_of_hours'];
export const SITE_FLAG_LABELS = { asbestos: 'Asbestos', restricted: 'Restricted area', lift: 'Lift', parking: 'Parking', out_of_hours: 'Out-of-hours access' };

// The basis a job is priced on unless the survey says otherwise (the last
// page of the paper form).
export const STANDARD_ASSUMPTIONS = [
  'Standard working hours, single visit, no MEWP required',
  'Power and network points already in place and live',
  'Area clear, no permit and no escort required',
  'Existing containment reused where possible',
  'Cabling contained and tested to HSE standard',
  'No making good or decorating, and no furniture moves',
];

/** A comma list column as an array of known values. */
export function listOf(value, allowed) {
  const parts = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  return allowed ? parts.filter(p => allowed.includes(p)) : parts;
}

/**
 * What this survey says is not standard: each entry changes the price. Blank
 * answers are standard, so they never appear here.
 */
export function deviations(s) {
  if (!s) return [];
  const out = [];
  if (s.can_do === 'differently') out.push(`Can be done, but differently${s.can_do_instead ? `: ${s.can_do_instead}` : ''}`);
  if (s.can_do === 'no') out.push(`Cannot be done as asked${s.can_do_instead ? `: ${s.can_do_instead}` : ''}`);
  if (s.hours_window === 'out') out.push('Out of hours working');
  if (s.visit_plan === 'phased') out.push(`Phased visits${s.visit_plan_reason ? `: ${s.visit_plan_reason}` : ''}`);
  if (s.height_access && s.height_access !== 'no') out.push(`Working at height: ${HEIGHT_LABELS[s.height_access]}`);
  if (s.power_ok === 'no') out.push(`Power not in place${s.power_notes ? `: ${s.power_notes}` : ''}`);
  if (s.network_ok === 'no') out.push(`Network points not in place or not live${s.network_notes ? `: ${s.network_notes}` : ''}`);
  if (s.area_clear === 'no') out.push(`Area not clear${s.area_notes ? `: ${s.area_notes}` : ''}`);
  if (s.permits_needed === 'yes') {
    const p = listOf(s.permits, PERMITS).map(k => PERMIT_LABELS[k]);
    out.push(`Needs ${p.length ? p.join(', ') : 'a permit or escort'}`);
  }
  const flags = listOf(s.site_flags, SITE_FLAGS).map(k => SITE_FLAG_LABELS[k]);
  if (flags.length) out.push(`Site: ${flags.join(', ')}`);
  return out;
}

/** Engineer days the survey suggests: engineers on site times days each. */
export function engineerDays(s) {
  const e = Number(s?.engineers), d = Number(s?.days_each);
  return e > 0 && d > 0 ? e * d : null;
}

/**
 * Why this survey cannot be submitted yet, as sentences to show the engineer,
 * or an empty list when it can. `lines` is the survey's lines.
 */
export function submitProblems(s, lines = []) {
  const problems = [];
  if (!s?.can_do) problems.push('Say whether it can be done as asked (question 2).');
  if ((s?.can_do === 'differently' || s?.can_do === 'no') && !String(s?.can_do_instead || '').trim()) {
    problems.push('Say what you would do instead (question 2).');
  }
  if (!lines.some(l => l.section === 'task' && String(l.item || '').trim())) problems.push('List at least one task under What needs doing (question 3).');
  if (!(Number(s?.engineers) > 0) || !(Number(s?.days_each) > 0)) problems.push('Give the number of engineers and the days each (question 7).');
  return problems;
}

/** Does this opportunity need a survey? The PM's own call, else yes for PI/TI work. */
export const surveyRequiredByDefault = (type) => String(type || '').trim().toUpperCase() === 'PI/TI';
export function surveyRequired(sow) {
  if (sow?.survey_required === 1 || sow?.survey_required === true) return true;
  if (sow?.survey_required === 0 || sow?.survey_required === false) return false;
  return surveyRequiredByDefault(sow?.type);
}

/**
 * One line for an opportunity's surveys on the tracker: `required` from
 * surveyRequired, `counts` as { status: n }. Returns { text, tone } or null
 * when there is nothing to say.
 */
export function surveySummary(required, counts = {}) {
  const live = STATUSES.filter(s => s !== 'cancelled').reduce((n, s) => n + (counts[s] || 0), 0);
  if (!live) return required ? { text: 'Needed', tone: 'bg-amber-50 text-amber-700' } : null;
  const reviewed = counts.reviewed || 0;
  if (reviewed === live) return { text: live === 1 ? 'Reviewed' : `${live} reviewed`, tone: STATUS_TONES.reviewed };
  const waiting = counts.submitted || 0;
  if (waiting) return { text: `${waiting} to review`, tone: STATUS_TONES.submitted };
  return { text: `${reviewed} of ${live} reviewed`, tone: STATUS_TONES.in_progress };
}

// ── Desk surveys (Phase 2, migration 0409) ───────────────────────────────────
//
// A desk survey is the per-desk record kept for desk moves and installs: the
// agreed standards (what kit and which networks each kind of desk gets), one
// row per desk, and the adjustments, additional equipment and risks found on
// the walk. Everything a workbook formula used to work out is worked out here,
// from the rows, by the same code on the server and the screen.

export const DESK_NETWORKS = ['RDN', 'RDC', 'NSE', 'BB'];
export const DEFAULT_KIT_ITEMS = ['Dock', 'Monitor', 'Keyboard', 'Mouse', 'Laptop riser', 'Dock lock', 'KVM'];
export const EXTRA_KINDS = ['adjustment', 'additional', 'risk', 'issue'];
export const EXTRA_LABELS = {
  adjustment: 'Specialist equipment and workplace adjustments',
  additional: 'Additional equipment beyond the standard kit',
  risk: 'Risks and early-detection checklist',
  issue: 'Equipment, adjustments and move issues',
};
export const RAG_LABELS = { red: 'Red', amber: 'Amber', green: 'Green' };

// The checklist a new desk survey starts with, from the desk survey workbook.
// Each is a prompt to check on the walk, not a finding: the finding and the
// RAG are what the engineer records.
export const DEFAULT_DESK_RISKS = [
  ['Too few sockets at the desks with the most kit', 'Extension leads may breach site rules', 'Free sockets against sockets needed at each desk'],
  ['Too few RDC or NSE switch ports', 'A desk cannot connect on the day', 'Spare ports per network at the switch'],
  ['RDC or NSE machines not ready', 'Outside the project, so those desks cannot be completed', 'Request or rebuild status per user'],
  ['NSE security rules', 'Moving or re-cabling NSE machines may need approval or cleared staff', 'Client security owner and approval process'],
  ['Desk too small for three monitors', 'Rework or monitor arms needed', 'Desk width and depth, and the need for monitor arms'],
  ['New docks incompatible with laptops or existing monitors', 'Rework on install day', 'Laptop models and monitor video inputs (DisplayPort, HDMI, USB-C)'],
  ['Dead, unpatched or mislabelled outlets', 'A desk cannot connect on the day', 'Test or trace each port recorded'],
  ['Floor boxes blocked by pedestals or furniture', 'Access delays at install', 'Floor box access at every desk'],
  ['Cable too short on sit/stand desks', 'Cables pulled or damaged when the desk rises', 'Full height travel with cables routed'],
  ['Kit sent to the wrong user', 'Duplicate names on the allocation', 'Surnames for every desk'],
  ['Old equipment removal', 'Storage, disposal and any data-bearing devices', 'Who owns removal and disposal'],
  ['Allocation changes after the survey', 'The requirement may change before sign-off', 'The date the final requirement will be issued'],
];

/** The kit items a desk survey counts, in order. */
export const kitItemsOf = (s) => {
  const items = listOf(s?.desk_kit_items);
  return items.length ? items : DEFAULT_KIT_ITEMS;
};

/** A JSON column as an object, whatever state it arrives in. */
export function mapOf(v) {
  if (!v) return {};
  if (typeof v === 'object') return v;
  try { const o = JSON.parse(v); return o && typeof o === 'object' && !Array.isArray(o) ? o : {}; } catch { return {}; }
}

const num = (v) => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null);

/** Is a port answer a port? Blank, "No", "N/A" and "None" are not. */
export const hasPort = (v) => {
  const t = String(v ?? '').trim().toLowerCase();
  return !!t && !['no', 'n', 'n/a', 'na', 'none', '-', '0'].includes(t);
};

/**
 * One desk as it works out: its standard, the networks and sockets it needs,
 * the shortfall, its kit and anything missing. `standards` is the survey's.
 */
export function deskView(desk, standards = []) {
  const std = standards.find(s => s.key === desk.standard_key) || null;
  const networks = listOf(desk.networks).length ? listOf(desk.networks) : listOf(std?.networks);
  const required = num(desk.sockets_required) ?? num(std?.sockets);
  const available = num(desk.sockets_available);
  const shortfall = required !== null && available !== null ? available - required : null;
  const ownKit = mapOf(desk.kit_json);
  const stdKit = mapOf(std?.kit_json);
  const kit = { ...stdKit };
  for (const [k, v] of Object.entries(ownKit)) if (num(v) !== null) kit[k] = num(v);
  const ports = mapOf(desk.ports_json);
  const missing = networks.filter(n => !hasPort(ports[n]));
  return { std, networks, required, available, shortfall, kit, reusable: mapOf(desk.reusable_json), ports, missing };
}

/**
 * The survey's totals: kit required, already at the desks and to order; per
 * network, the desks that need it and the ports recorded; and the problems a
 * PM should see before pricing it.
 */
export function deskTotals(survey, standards = [], desks = []) {
  const items = kitItemsOf(survey);
  const kit = Object.fromEntries(items.map(i => [i, { required: 0, reusable: 0, order: 0 }]));
  const networks = Object.fromEntries(DESK_NETWORKS.map(n => [n, { desks: 0, ports: 0 }]));
  const problems = [];
  let socketsShort = 0, extraSockets = 0, unchecked = 0, noStandard = 0;
  for (const d of desks) {
    const v = deskView(d, standards);
    if (!d.checked) unchecked += 1;
    if (!v.std && !v.networks.length) noStandard += 1;
    for (const [item, q] of Object.entries(v.kit)) {
      if (!kit[item]) kit[item] = { required: 0, reusable: 0, order: 0 };
      kit[item].required += num(q) || 0;
    }
    for (const [item, q] of Object.entries(v.reusable)) {
      if (!kit[item]) kit[item] = { required: 0, reusable: 0, order: 0 };
      kit[item].reusable += num(q) || 0;
    }
    for (const n of v.networks) {
      networks[n] ||= { desks: 0, ports: 0 };
      networks[n].desks += 1;
      if (hasPort(v.ports[n])) networks[n].ports += 1;
    }
    if (v.shortfall !== null && v.shortfall < 0) {
      socketsShort += 1;
      problems.push(`Desk ${d.desk}: ${-v.shortfall} ${v.shortfall === -1 ? 'socket' : 'sockets'} short.`);
    }
    if (v.missing.length) problems.push(`Desk ${d.desk}: no ${v.missing.join(', ')} port recorded.`);
    if (d.access_issue === 'yes') problems.push(`Desk ${d.desk}: access issue${d.access_detail ? `, ${d.access_detail}` : ''}.`);
    extraSockets += num(d.extra_sockets) || 0;
  }
  for (const k of Object.values(kit)) k.order = Math.max(0, k.required - k.reusable);
  return { kit, networks, problems, socketsShort, extraSockets, unchecked, noStandard, desks: desks.length };
}

/** Why a desk survey cannot be submitted yet, or an empty list. */
export function deskSubmitProblems(s, desks = [], standards = []) {
  const problems = [];
  if (!desks.length) problems.push('Add the desks surveyed.');
  const unchecked = desks.filter(d => !d.checked).map(d => d.desk);
  if (unchecked.length) problems.push(`Say whether each desk was checked (${unchecked.slice(0, 6).join(', ')}${unchecked.length > 6 ? ` and ${unchecked.length - 6} more` : ''}).`);
  const unset = desks.filter(d => !deskView(d, standards).std && !listOf(d.networks).length).map(d => d.desk);
  if (unset.length) problems.push(`Give each desk a standard (${unset.slice(0, 6).join(', ')}${unset.length > 6 ? ` and ${unset.length - 6} more` : ''}).`);
  if (!(Number(s?.engineers) > 0) || !(Number(s?.days_each) > 0)) problems.push('Give the number of engineers and the days each.');
  return problems;
}

/** Why this survey, of either type, cannot be submitted yet. */
export function problemsFor(s, parts = {}) {
  const kind = kindOf(s);
  if (kind === 'moves') return moveSubmitProblems(s, parts.moves || []);
  if (kind === 'desks') return deskSubmitProblems(s, parts.desks || [], parts.standards || []);
  return submitProblems(s, parts.lines || []);
}

// ── Desk moves (migration 0410) ──────────────────────────────────────────────
//
// One row per person, desk, team or piece of equipment that moves, as the
// team's Desk Moves workbook keeps them, plus the port at each end and whether
// it is live, and monitor arms. The survey (readiness at both ends) is
// submitted and reviewed like any other; after review only the move-day
// fields stay open, so the moves can be ticked off on the day.

export const MOVE_NETWORKS = ['RDN', 'RDC', 'NSE'];
export const MOVE_ENDS = ['from', 'to'];
export const MOVE_STATUSES = ['not_started', 'ready', 'in_progress', 'blocked', 'complete'];
export const MOVE_STATUS_LABELS = { not_started: 'Not started', ready: 'Ready', in_progress: 'In progress', blocked: 'Blocked', complete: 'Complete' };
export const MOVE_STATUS_TONES = {
  not_started: 'bg-gray-100 text-gray-600', ready: 'bg-blue-50 text-blue-700', in_progress: 'bg-amber-50 text-amber-700',
  blocked: 'bg-red-50 text-red-700', complete: 'bg-green-50 text-green-700',
};
export const PRIORITY_LABELS = { critical: 'Critical', high: 'High', normal: 'Normal', low: 'Low' };
export const MOVE_TYPE_LABELS = { person: 'Person', equipment: 'Equipment', desk: 'Desk', team: 'Team' };
export const CHECK_LABELS = { yes: 'Yes', no: 'No', not_checked: 'Not checked' };
export const PHASES = [1, 2, 3, 4];
// The kit that travels with a move, each yes or no, as the workbook's columns.
export const MOVE_KIT = ['Dock & PSU', 'Keyboard / Mouse', 'Headset / Phone', 'Power & Cables', 'Adaptors', 'Special / Accessibility Kit', 'Asset Labels Checked'];
export const ISSUE_STATUS_LABELS = { open: 'Open', in_progress: 'In progress', blocked: 'Blocked', closed: 'Closed' };
export const RISK_STATUS_LABELS = { open: 'Open', in_progress: 'In progress', mitigated: 'Mitigated', closed: 'Closed' };
export const MOVE_OUTCOME_LABELS = { ready: 'Ready to use', ready_actions: 'Ready subject to actions', not_ready: 'Not ready' };
// What may still change once a move survey is reviewed: move day.
export const MOVE_DAY_FIELDS = ['status', 'blocker', 'move_complete', 'user_check', 'packed_by', 'collated_date', 'staging_location', 'missing_kit', 'kit_status'];

/** MV-001, MV-002: the workbook's move numbering. */
export const moveRef = (n) => `MV-${String(n).padStart(3, '0')}`;

/** One move as it works out: the networks it needs, its ports and what stands in its way. */
export function moveView(m) {
  const networks = listOf(m.networks).filter(n => MOVE_NETWORKS.includes(n));
  const ports = mapOf(m.ports_json);
  const at = (end, net) => mapOf(ports[end])[net] ? mapOf(mapOf(ports[end])[net]) : {};
  const issues = [];
  if (m.dest_ready === 'no') issues.push('destination not ready');
  if (m.network_ready === 'no') issues.push('network not ready');
  if (m.power_ready === 'no') issues.push('power not ready');
  for (const net of networks) {
    const to = at('to', net);
    if (!hasPort(to.port)) issues.push(`no ${net} port at the new desk`);
    else if (to.live !== 'yes') issues.push(`${net} port ${to.port} not confirmed live`);
  }
  if (m.status === 'blocked') issues.push(m.blocker ? `blocked: ${m.blocker}` : 'blocked');
  return { networks, at, issues };
}

/** The move job's summary, as the workbook's Move Summary tab gave it, and what to resolve. */
export function moveTotals(moves = [], extras = []) {
  const byStatus = Object.fromEntries(MOVE_STATUSES.map(k => [k, 0]));
  const byPhase = Object.fromEntries(PHASES.map(k => [k, 0]));
  const byPriority = Object.fromEntries(Object.keys(PRIORITY_LABELS).map(k => [k, 0]));
  const notReady = { dest: 0, network: 0, power: 0 };
  let userChecks = 0, monitors = 0, monitorArms = 0;
  const problems = [];
  for (const m of moves) {
    if (m.status) byStatus[m.status] = (byStatus[m.status] || 0) + 1;
    else byStatus.not_started += 1;
    if (byPhase[m.phase] !== undefined) byPhase[m.phase] += 1;
    if (m.priority) byPriority[m.priority] += 1;
    if (m.dest_ready === 'no') notReady.dest += 1;
    if (m.network_ready === 'no') notReady.network += 1;
    if (m.power_ready === 'no') notReady.power += 1;
    if (m.user_check === 'yes') userChecks += 1;
    if (m.monitors !== 'no') monitors += Number(m.monitor_qty) || 0;
    if (m.monitor_arms === 'yes') monitorArms += Number(m.monitor_arm_qty) || 0;
    const v = moveView(m);
    if (v.issues.length) problems.push(`${m.move_ref}${m.user_name ? ` (${m.user_name})` : ''}: ${v.issues.join(', ')}.`);
  }
  const openIssues = extras.filter(e => e.kind === 'issue' && e.status !== 'closed').length;
  const redAmber = extras.filter(e => e.kind === 'risk' && (e.rag === 'red' || e.rag === 'amber')).length;
  const planned = moves.length;
  return {
    planned, byStatus, byPhase, byPriority, notReady, userChecks, monitors, monitorArms, openIssues, redAmber, problems,
    completion: planned ? Math.round((byStatus.complete / planned) * 100) : 0,
  };
}

/** Why a desk move survey cannot be submitted yet, or an empty list. */
export function moveSubmitProblems(s, moves = []) {
  const problems = [];
  const few = (list) => `${list.slice(0, 6).join(', ')}${list.length > 6 ? ` and ${list.length - 6} more` : ''}`;
  if (!moves.length) problems.push('Add the moves.');
  const nowhere = moves.filter(m => !String(m.to_desk || '').trim() && !String(m.to_area || '').trim()).map(m => m.move_ref);
  if (nowhere.length) problems.push(`Say where each move goes (${few(nowhere)}).`);
  const unchecked = moves.filter(m => !m.dest_ready || !m.network_ready || !m.power_ready).map(m => m.move_ref);
  if (unchecked.length) problems.push(`Answer destination, network and power ready for each move, Not checked if you could not (${few(unchecked)}).`);
  if (!(Number(s?.engineers) > 0) || !(Number(s?.days_each) > 0)) problems.push('Give the number of engineers and the days each.');
  return problems;
}

// ── The forms Ops issues (authoritative templates) ───────────────────────────
//
// Ops is the one source of the survey forms: the Word form for a room, and the
// workbooks for a desk standardisation and a desk move. Each is downloaded
// from Ops, blank or for one survey, filled in and uploaded back. Every form
// carries this stamp, so an upload is read as the right kind of form and
// lands on the survey it was issued for. Raise FORM_VERSION when a form's
// layout changes, so a form downloaded before the change is noticed.
export const FORM_VERSION = '2026-10-26.2';

/** The stamp printed on a form: its kind, version and survey (none on a blank form). */
export const formStamp = (kind, reference) =>
  `Alton Valley Ops form: ${KIND_LABELS[kind]}, version ${FORM_VERSION}, survey ${reference || 'not yet raised'}.`;

/** The stamp read back from a form's text, or null when the text carries none. */
export function readFormStamp(text) {
  const m = String(text || '').match(/Alton Valley Ops form: (.+?), version ([\w.-]+), survey (SVY-\d+|not yet raised)/);
  if (!m) return null;
  const kind = Object.entries(KIND_LABELS).find(([, l]) => l === m[1])?.[0] || null;
  return { kind, version: m[2], reference: m[3].startsWith('SVY-') ? m[3] : null };
}
